
import { localStorage } from '../local-storage/LocalStorage';
import metadefenderService from '../metadefender/MetadefenderService';
import BackendService from '../backend-service/BackendService';
// Using conditional require statement because otherwise the `mocli dev` command will fail
// when generating the html.js because FingerprintJS uses window object which is not available during build
const FingerprintJS = typeof window !== 'undefined' ? require('@fingerprintjs/fingerprintjs') : null;
import Cookies from 'js-cookie';
import { COOKIE_AUTH, COOKIE_OPSWAT_REDIRECT } from '@mdc/constants';
import { i18nT } from '@mdc/services';

class AuthHelper {
    /**
     * @return {string|null} CSRF token
     */
    getCsrfToken() {
        /* global METADEFENDER_TOKEN */
        if (typeof (METADEFENDER_TOKEN) !== 'undefined') {
            return METADEFENDER_TOKEN;
        }
        return null;
    }

    /**
     * @param {string} input input string
     * @return {string} base64 decoded string
     */
    b64DecodeUnicode(input) {
        return decodeURIComponent(Array.prototype.map.call(atob(input), (c) => {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
    }

    /**
     * Configure __Host-opswat-auth cookie
     * @param {string} apikey
     * @param {boolean} isLoggedIn
     */
    setAuthCookie(apikey, isLoggedIn) {
        const currentCookie = this.getAuthCookie();
        const shouldUpdateAuthCookie = currentCookie && currentCookie.apikey && apikey && currentCookie.apikey !== apikey;

        if (shouldUpdateAuthCookie || !currentCookie) {
            Cookies.set(COOKIE_AUTH, JSON.stringify({
                apikey: apikey,
                loggedIn: isLoggedIn
            }), {
                path: '/',
                expires: 15,  // days
                sameSite: 'lax',
                secure: true,
                httpOnly: true
            });
        }
    }

    /**
     * @return {object|null} sso object from window.SSO
     */
    checkSSO() {
        if (window.SSO) {
            const sso = JSON.parse(this.b64DecodeUnicode(window.SSO));
            return (sso) ? sso : null;
        }
        return null;
    }

    /**
     * @param {string} apikey input apikey
     * @return {Promise<string>} apikey from backend
     */
    async createApiKey(apikey) {
        if (this.getCsrfToken()) {
            try {
                const [addApiKeyPromise] = await BackendService.addApiKey(apikey, {}, {});
                const addApiKey = await addApiKeyPromise;

                if (addApiKey.data.key) {
                    return addApiKey.data.key;
                }
            }
            catch (error) {
                return; // suppres error for now because we are making two createaApikey requests
                // if ([400002, 400025].indexOf(error.code) !== -1) {
                //     throw new Error(i18nT('Failed to create your API key'));
                // }
            }
            throw new Error(i18nT('Failed to create your API key'));

        } else {
            throw new Error(i18nT('Csrf-token not found'));
        }
    }

    /**
     * @return {Promise<string>} apikey generated by fingerprintjs
     */
    async getFingerPrint() {
        if (!FingerprintJS) {
            return;
        }

        const fp = await FingerprintJS.load();
        const result = await fp.get();
        const apikey = result.visitorId;

        if (!apikey) {
            throw new Error(i18nT('Failed to generate a Fingerprint for you'));
        }

        return apikey;

    }

    /**
     * @return {Promise<Object>} apikey object from db
     */
    async checkApiKey(apikey) {
        try {
            const [getInfoPromise] = metadefenderService.apikey.getInfo({ apikey });
            const data = (await getInfoPromise).data;
            if (data.portal_api_key) {
                this.setAuthCookie(data.portal_api_key, localStorage.getItem('loggedIn') ? true : false);
            }
            return data;
        } catch (error) {
            if (error.response) {
                if ([401, 404].includes(error.response.status)) {
                    return this.createApiKey(await this.getFingerPrint());
                }
                if (error.response.data.error.code === 403004) {
                    return { ipRestriction: true };
                }
                throw new Error(i18nT('A server error occurred, and your request could not be completed. Please refresh the page'));
            }
            throw new Error(i18nT('Network error occurred, and your request could not be completed. Please refresh the page'));
        }
    }

    /**
     * @return {{apikey: string, loggedIn: boolean}|null} authCookie object
     */
    getAuthCookie() {
        const authCookie = Cookies.get(COOKIE_AUTH);
        if (!authCookie) {
            return null;
        }

        return JSON.parse(decodeURIComponent(authCookie));
    }

    /**
     * @return {string} csrf-token
     */
    initCsrfToken() {
        const token = this.getCsrfToken();
        if (token) {
            BackendService.addCommonHeader({ 'X-Csrf': token });
            return token;
        }
        throw new Error(i18nT('Csrf-token not found'));
    }

    /**
     * @param {number} redirectTimeout ms to wait before redirect
     */
    async logout(redirectTimeout) {
        redirectTimeout = redirectTimeout || 0;
        localStorage.setItem('loggedIn', false);

        // generate new apikey
        this.setAuthCookie(await this.getFingerPrint(), false);

        setTimeout(() => {
            window.location.href = '/logout';
        }, redirectTimeout);
    }

    redirect(url) {
        Cookies.set(COOKIE_OPSWAT_REDIRECT, window.location.href, { secure: true });
        window.location.href = url;
    }
}

export const authHelper = new AuthHelper();
